/**
 * Planification des rappels sur l'appareil
 *
 * L'application Android ne peut pas recevoir de Web Push : la WebView de
 * Capacitor n'implémente pas l'API Push. Les rappels du téléphone sont donc
 * **programmés localement**, à l'avance, comme le fait déjà l'autre application
 * de la maison. Aucun serveur n'envoie rien, ce qui a trois conséquences
 * heureuses — ça marche hors ligne, sans Firebase, et sans dépendre de la
 * disponibilité du serveur — et deux limites qu'il faut dire à l'utilisateur :
 * seuls les appareils où l'application est installée sont prévenus, et un rappel
 * déjà programmé ignore une modification faite ailleurs jusqu'à la prochaine
 * resynchronisation.
 *
 * Ce fichier ne contient que le **calcul** : quelles notifications, à quelle
 * date, avec quel identifiant. L'appel au greffon vit dans le front. C'est ce
 * découpage qui rend la partie délicate testable sans téléphone.
 *
 * Les offsets de rappel viennent des tâches elles-mêmes, via `datesDeRappel` —
 * le même code que les canaux serveur. Une seconde logique de calcul aurait
 * divergé du premier jour où l'une des deux aurait été corrigée.
 */
import { ajouteJours, enClair, joursEntre } from './dates.js';
import { estJourDeRelance } from './rappels.js';
import { datesDeRappel, occurrencesEntre } from './recurrence.js';
/**
 * Android refuse de garder un nombre illimité de notifications en attente — la
 * limite tourne autour de 500 selon les versions, et l'on ne veut pas la
 * frôler. Un foyer de onze équipements et quatre personnes produit déjà plus de
 * cent tâches, chacune avec deux à trois offsets de rappel : sans plafond, une
 * seule synchronisation dépasserait la limite et Android jetterait
 * silencieusement le surplus, en gardant les mauvaises.
 */
export const MAX_NOTIFICATIONS = 400;
/** Au-delà, ce n'est plus un rappel, c'est de l'archivage. */
export const HORIZON_JOURS = 120;
/**
 * Base des identifiants. Le front ne doit annuler que *ses* notifications :
 * comparer à cette base évite d'effacer celles qu'un autre greffon aurait
 * posées.
 */
export const BASE_ID = 10_000;
/** Identifiant réservé à la notification d'essai, hors de la plage gérée. */
export const ID_ESSAI = 9_999;
/**
 * Plafond de relances programmées d'avance, par tâche.
 *
 * Un rythme quotidien sur dix tâches en retard produirait plus de mille
 * notifications sur l'horizon, qui mangeraient tout le budget des 400 et
 * évinceraient les rappels d'échéance des autres tâches — l'inverse du service
 * rendu.
 *
 * Huit suffit parce que le plan est recalculé à **chaque ouverture** de
 * l'application : programmer trois mois de relances d'avance ne sert que la
 * personne qui n'ouvre jamais l'application, et huit relances l'atteignent déjà
 * (huit jours en quotidien, deux mois en hebdomadaire).
 */
export const MAX_RELANCES_PAR_TACHE = 8;
/**
 * Identifiant déterministe pour un couple (tâche, date de rappel).
 *
 * Il doit être stable d'une synchronisation à l'autre : si l'identifiant
 * changeait, annuler puis reprogrammer ferait réapparaître un rappel déjà
 * affiché. Et il doit tenir dans un entier 32 bits signé, seul format accepté
 * par Android.
 */
export function identifiantNotification(tacheId, date) {
    const graine = `${tacheId}@${date}`;
    let h = 2166136261;
    for (let i = 0; i < graine.length; i++) {
        h ^= graine.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    // On reste dans une plage positive au-dessus de BASE_ID, sous 2^31.
    return BASE_ID + (Math.abs(h) % (2_000_000_000 - BASE_ID));
}
function corpsDe(echeance, offsetJours) {
    // Offset négatif = relance sur une tâche en retard, comme dans le moteur
    // serveur. Le texte dit le retard, pas l'échéance : « en retard de 3 jours »
    // se comprend d'un coup d'œil sur un écran verrouillé, une date pas toujours.
    if (offsetJours < 0) {
        const retard = -offsetJours;
        return retard === 1
            ? `En retard d'un jour — échéance du ${enClair(echeance)}.`
            : `En retard de ${retard} jours — échéance du ${enClair(echeance)}.`;
    }
    if (offsetJours === 0)
        return `C'est aujourd'hui — échéance du ${enClair(echeance)}.`;
    if (offsetJours === 1)
        return `Demain — échéance du ${enClair(echeance)}.`;
    return `Dans ${offsetJours} jours — échéance du ${enClair(echeance)}.`;
}
/**
 * Établit la liste des notifications à programmer.
 *
 * Ne rend jamais de rappel dans le passé : une notification programmée pour
 * hier serait soit ignorée par Android, soit déclenchée immédiatement, ce qui
 * est pire — on réveille l'utilisateur pour une échéance qu'il a déjà vue.
 */
export function planifieRappelsLocaux(taches, options) {
    const { aujourdhui } = options;
    const horizon = ajouteJours(aujourdhui, options.horizonJours ?? HORIZON_JOURS);
    const max = options.max ?? MAX_NOTIFICATIONS;
    const periodes = options.periodes ?? [];
    const brut = [];
    for (const t of taches) {
        if (t.statut !== 'active' || !t.echeance)
            continue;
        if (t.rappels.length === 0)
            continue;
        const recurrence = {
            rrule: t.rrule,
            depart: t.recurrenceDepart,
            base: t.recurrenceBase,
            ancrage: t.ancrageVacances,
        };
        // Les occurrences à venir dans l'horizon, répétitions comprises : une tâche
        // mensuelle doit prévenir chaque mois, pas une seule fois.
        const occurrences = occurrencesEntre(recurrence, t.echeance, aujourdhui, horizon, periodes);
        for (const echeance of occurrences) {
            // La fenêtre de résiliation, quand elle existe, est la vraie référence :
            // rappeler le jour de l'échéance d'une assurance est inutile, il est trop
            // tard pour résilier.
            for (const r of datesDeRappel(echeance, t.rappels, t.resiliationLimite)) {
                if (r.date < aujourdhui || r.date > horizon)
                    continue;
                brut.push({
                    id: identifiantNotification(t.id, r.date),
                    tacheId: t.id,
                    titre: t.titre,
                    corps: corpsDe(echeance, r.offsetJours),
                    date: r.date,
                    echeance,
                    offsetJours: r.offsetJours,
                });
            }
        }
    }
    // ── Relances sur les tâches déjà en retard ────────────────────────────────
    //
    // Elles manquaient entièrement : le planificateur embarqué ne posait que les
    // rappels d'AVANT échéance. Sur Android — seul canal réellement reçu, faute
    // d'API Push dans la WebView — une tâche en retard ne disait donc plus rien,
    // et le rythme de relance réglé dans l'application n'avait aucun effet visible.
    //
    // On ne traite que les tâches en retard aujourd'hui : une relance sur une
    // échéance encore à venir n'a pas de sens, et l'occurrence suivante d'une
    // tâche récurrente sera reprogrammée à la prochaine ouverture.
    //
    // Le seuil d'escalade est volontairement ignoré ici : escalader, c'est
    // prévenir l'autre parent, ce qu'une notification locale ne sait pas faire.
    // Elle ne parle qu'à la personne qui tient le téléphone.
    const relance = options.relance ?? 'hebdomadaire';
    for (const t of taches) {
        if (t.statut !== 'active' || !t.echeance)
            continue;
        if (t.echeance >= aujourdhui)
            continue;
        let posees = 0;
        for (let jour = aujourdhui; jour <= horizon && posees < MAX_RELANCES_PAR_TACHE; jour = ajouteJours(jour, 1)) {
            const retard = joursEntre(t.echeance, jour);
            if (!estJourDeRelance(retard, null, t.relanceMuette ? 'jamais' : relance))
                continue;
            brut.push({
                id: identifiantNotification(t.id, jour),
                tacheId: t.id,
                titre: t.titre,
                corps: corpsDe(t.echeance, -retard),
                date: jour,
                echeance: t.echeance,
                offsetJours: -retard,
            });
            posees++;
        }
    }
    // Le plus proche d'abord : si le plafond coupe, il coupe le lointain.
    brut.sort((a, b) => (a.date === b.date ? a.titre.localeCompare(b.titre, 'fr') : a.date.localeCompare(b.date)));
    // Dédoublonnage par identifiant : deux occurrences d'une même tâche peuvent
    // partager une date de rappel quand les offsets se recouvrent (un rappel à
    // 45 jours sur une règle mensuelle). Android n'accepte pas deux notifications
    // de même identifiant — la seconde écrase la première en silence.
    const vues = new Set();
    const notifications = [];
    for (const n of brut) {
        if (vues.has(n.id))
            continue;
        vues.add(n.id);
        notifications.push(n);
    }
    return {
        notifications: notifications.slice(0, max),
        ecartees: Math.max(0, notifications.length - max),
    };
}
/** Prochaine notification du plan, pour l'afficher dans le diagnostic. */
export function prochaineNotification(plan) {
    return plan.notifications[0] ?? null;
}
/** Combien de jours avant la première notification programmée. */
export function joursAvantProchaine(plan, aujourdhui) {
    const p = prochaineNotification(plan);
    return p ? joursEntre(aujourdhui, p.date) : null;
}
//# sourceMappingURL=rappels-locaux.js.map